// TargetAllocationModal — edit per-client target asset-class allocation.
import { useEffect, useState } from 'react';
import { X, Save, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_CLASSES = [
  'Cash', 'Australian Equities', 'International Equities', 'Fixed Income',
  'Property', 'Alternatives', 'Private Markets',
];

export default function TargetAllocationModal({ clientId, onClose, onSaved }) {
  const { token } = useAuth();
  const [rows, setRows] = useState(ASSET_CLASSES.map((c) => ({ assetClass: c, targetPercent: 0 })));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/clients/${clientId}/target-allocation`, { headers: { Authorization: `Bearer ${token}` } });
        const body = await res.json();
        const byClass = {};
        (body.allocations || []).forEach((a) => { byClass[a.assetClass] = (byClass[a.assetClass] || 0) + (a.targetPercent || 0); });
        setRows(ASSET_CLASSES.map((c) => ({ assetClass: c, targetPercent: byClass[c] || 0 })));
      } catch (e) {
        logger.warn('TargetAllocationModal load failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId, token]);

  const setPct = (i, v) => {
    const next = parseFloat(v);
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, targetPercent: Number.isNaN(next) ? 0 : next } : row)));
  };

  const total = rows.reduce((s, r) => s + (r.targetPercent || 0), 0);
  const overBy = Math.max(0, total - 100);

  const save = async () => {
    if (total > 100.01) { toast.error(`Total is ${total.toFixed(1)}% — must be ≤ 100%`); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/clients/${clientId}/target-allocation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ allocations: rows.filter((r) => r.targetPercent > 0) }),
      });
      if (!res.ok) throw new Error(await res.text());
      onSaved?.();
    } catch (e) {
      logger.warn('TargetAllocationModal save failed', e);
      toast.error('Could not save target allocation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="target-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E0D9]">
          <h3 className="font-heading text-lg text-[#2C2420]">Target allocation</h3>
          <button onClick={onClose} className="text-[#5D524D] hover:text-[#2C2420]" data-testid="target-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" /></div>
          ) : (
            rows.map((r, i) => (
              <div key={r.assetClass} className="flex items-center gap-3" data-testid={`target-row-${r.assetClass.replace(/\s+/g, '-').toLowerCase()}`}>
                <span className="text-sm text-[#2C2420] flex-1 min-w-0 truncate">{r.assetClass}</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    value={r.targetPercent}
                    onChange={(e) => setPct(i, e.target.value)}
                    min="0"
                    max="100"
                    step="0.5"
                    className="w-20 h-8 text-right"
                    data-testid={`target-input-${i}`}
                  />
                  <span className="text-sm text-[#8C8078]">%</span>
                </div>
              </div>
            ))
          )}
          <div className={cn('mt-4 pt-3 border-t border-[#E6E0D9] flex items-center justify-between text-sm', overBy > 0.01 ? 'text-red-600' : total > 99.5 && total <= 100.01 ? 'text-green-700' : 'text-[#5D524D]')}>
            <span className="font-medium">Total</span>
            <span className="tabular-nums" data-testid="target-total">{total.toFixed(1)}% {overBy > 0.01 && <AlertTriangle className="w-3.5 h-3.5 inline" />}</span>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#E6E0D9] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="target-modal-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || overBy > 0.01} className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white" data-testid="target-modal-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save targets
          </Button>
        </div>
      </div>
    </div>
  );
}
